import { useMemo } from "react";

import { resizeCanvasToMinHeight } from "../utils";

/**
 * This minimum image height is used to upscale an image if it's small so on
 * zoom it doesn't look blurry.
 */
const MINIMUM_CANVAS_RENDERED_IMAGE_HEIGHT = 2160;

/**
 * Returns an optimized canvas for zooming if enabled.
 *
 * If `canZoom` is `true`, this hook returns a new canvas
 * resized to a minimum height (for high-resolution rendering).
 * If `canZoom` is `false`, it returns the original canvas unchanged.
 * If `originalCanvas` is `undefined`, the hook returns `undefined`.
 *
 * @param originalCanvas - The original canvas to optimize.
 * @param canZoom - Whether zoom optimization should be applied.
 * @returns The optimized canvas, original canvas, or `undefined`.
 */
export const useZoomOptimizedCanvas = (originalCanvas: HTMLCanvasElement | undefined, canZoom: boolean) => {
  /**
   * Generates optimized canvas for zooming when zoom is enabled.
   */
  const optimizedCanvas = useMemo(() => {
    if (!originalCanvas) {
      return undefined;
    }

    return canZoom ? resizeCanvasToMinHeight(originalCanvas, MINIMUM_CANVAS_RENDERED_IMAGE_HEIGHT) : originalCanvas;
  }, [originalCanvas, canZoom]);

  return optimizedCanvas;
};
